/// StatusBar: Bottom status bar (24px) displaying engine worker status,
/// active instance count, cursor position, zoom level, and editor type.
/// Includes notification area with auto-fade.

import { useEffect, useState } from "react";
import { Wifi, Spline, ChevronUp, ChevronDown } from "lucide-react";
import { useEngineStore } from "@/stores/engineStore";
import { useEditorStore } from "@/stores/editorStore";
import { useUIStore, type Notification } from "@/stores/uiStore";
import { useAIStore } from "@/stores/aiStore";
import type { WorkerStatus } from "@/types/engine";

// ---------------------------------------------------------------------------
// Engine status indicator (always visible, binary green/red)
// ---------------------------------------------------------------------------

interface EngineIndicatorProps {
  label: string;
  status: WorkerStatus | undefined;
}

/**
 * Always-visible engine indicator. Shows green bullet when the engine worker
 * is registered and ready; red bullet for all other states (disconnected,
 * initializing, error, terminated).
 */
function EngineIndicator({ label, status }: EngineIndicatorProps) {
  const isLive = status === "ready";
  const colorClass = isLive ? "bg-green-400" : "bg-red-400";
  const tooltip = status ? `${label}: ${status}` : `${label}: disconnected`;

  return (
    <div className="flex items-center gap-1" title={tooltip}>
      <span className={`h-2 w-2 rounded-full ${colorClass}`} />
      <span className="text-zinc-400">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notification display
// ---------------------------------------------------------------------------

function NotificationArea() {
  const notifications = useUIStore((s) => s.notifications);
  const removeNotification = useUIStore((s) => s.removeNotification);
  const [visibleNotif, setVisibleNotif] = useState<Notification | null>(null);

  // Show the latest notification, auto-fade after 5s
  useEffect(() => {
    if (notifications.length === 0) {
      setVisibleNotif(null);
      return;
    }

    const latest = notifications[0];
    setVisibleNotif(latest);

    const duration = latest.duration ?? 5000;
    if (duration > 0) {
      const timer = setTimeout(() => {
        setVisibleNotif((current) => (current?.id === latest.id ? null : current));
        removeNotification(latest.id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [notifications, removeNotification]);

  if (!visibleNotif) return null;

  const colorMap = {
    info: "text-blue-400",
    success: "text-green-400",
    warning: "text-yellow-400",
    error: "text-red-400",
  };

  return <span className={`truncate ${colorMap[visibleNotif.type]}`}>{visibleNotif.message}</span>;
}

// ---------------------------------------------------------------------------
// StatusBar component
// ---------------------------------------------------------------------------

/**
 * Application status bar (24px).
 * Left: Engine worker status dots, active instance count, offline indicator.
 * Center: Notification area.
 * Right: Selection count, cursor position, zoom level, editor type.
 */
export function StatusBar() {
  const workers = useEngineStore((s) => s.workers);
  const runningInstances = useEngineStore((s) => s.runningInstances);
  const bottomPanelVisible = useUIStore((s) => s.bottomPanelVisible);
  const toggleBottomPanel = useUIStore((s) => s.toggleBottomPanel);
  const aiStreaming = useAIStore((s) => s.isStreaming);

  const activeTab = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.tabs.find((t) => t.id === id) : undefined;
  });
  const editorState = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.editorStates.get(id) : undefined;
  });

  const connectionHint = useEditorStore((s) => s.connectionHint);

  // Compute engine worker statuses (may be undefined if not yet registered)
  const bpmnEngineStatus = Array.from(workers.values()).find(
    (w) => w.role === "bpmn-engine",
  )?.status;
  const nodeEngineStatus = Array.from(workers.values()).find(
    (w) => w.role === "node-engine",
  )?.status;

  // Active instance count
  const activeInstances = runningInstances.filter((i) => i.state === "running").length;

  // Selection count
  const selectionCount = editorState?.selection.selectedIds.length ?? 0;

  // Viewport info
  const zoom = editorState?.viewport.zoom ?? 1.0;
  const zoomPercent = Math.round(zoom * 100);
  const cursorX = Math.round(editorState?.viewport.centerX ?? 0);
  const cursorY = Math.round(editorState?.viewport.centerY ?? 0);

  // Editor type label
  const editorTypeLabel =
    activeTab?.editorType === "bpmn" ? "BPMN" : activeTab?.editorType === "nodeflow" ? "Node" : "";

  return (
    <div data-testid="status-bar" className="flex h-6 items-center border-t border-zinc-700 bg-zinc-800 px-3 text-[11px] gap-3 select-none">
      {/* Left: Worker status dots + instance count */}
      <div className="flex items-center gap-3 shrink-0">
        <EngineIndicator label="BPMN Engine" status={bpmnEngineStatus} />
        <EngineIndicator label="Node Engine" status={nodeEngineStatus} />

        {activeInstances > 0 && (
          <span className="text-zinc-400">
            {activeInstances} instance{activeInstances !== 1 ? "s" : ""}
          </span>
        )}

        {/* Offline-ready indicator */}
        <span className="text-zinc-500 flex items-center gap-0.5" title="Offline capable">
          <Wifi className="h-3 w-3" />
        </span>

        {/* Bottom panel toggle */}
        <button
          type="button"
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={toggleBottomPanel}
          aria-label={bottomPanelVisible ? "Hide bottom panel" : "Show bottom panel"}
        >
          {bottomPanelVisible ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronUp className="h-3 w-3" />
          )}
          <span>Console</span>
          {aiStreaming && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />}
        </button>
      </div>

      {/* Center: Connection hint (priority) or notification area */}
      <div className="flex-1 text-center truncate">
        {connectionHint ? (
          <span className="inline-flex items-center gap-1 text-zinc-300">
            <Spline className="h-3 w-3 text-blue-400 shrink-0" />
            {connectionHint}
          </span>
        ) : (
          <NotificationArea />
        )}
      </div>

      {/* Right: Selection, cursor, zoom, editor type */}
      <div className="flex items-center gap-3 shrink-0 text-zinc-400 tabular-nums">
        {selectionCount > 0 && <span>{selectionCount} selected</span>}

        {activeTab && (
          <>
            <span>
              X:{cursorX} Y:{cursorY}
            </span>
            <span>{zoomPercent}%</span>
            <span className="text-zinc-300 font-medium">{editorTypeLabel}</span>
          </>
        )}

        {!activeTab && <span className="text-zinc-500">Ready</span>}
      </div>
    </div>
  );
}
